import { useRef, useState } from "react";
import { Link } from "wouter";
import { Heart, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SearchStateLink, type SearchState } from "@/components/search/SearchStateLink";
import MustangLocationSearch from "./MustangLocationSearch";
import { buildCarPath, type CarCardData } from "@/lib/api";
import { getDisplayPrice } from "@/lib/displayPrice";
import { useCurrency } from "@/i18n/CurrencyContext";
import { useFavourite } from "@/hooks/useFavourite";
import snapshot from "@/content/mustangLiveSource.json";
import { resolveSourceSnapshot } from "@/lib/source-snapshot-brands";
import "./MustangSourceLanding.css";

const source = resolveSourceSnapshot(snapshot);

export const MUSTANG_LIVE_PATH = "/rentafordmustang";

export function isMustangLivePage(path: string | null | undefined): boolean {
  return `/${String(path || "").replace(/^\/+|\/+$/g, "")}` === MUSTANG_LIVE_PATH;
}

type Review = (typeof source.reviews)[number];

function ReviewCard({ review, index }: { review: Review; index: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const long = review.text.length > 120;
  const srcSet = review.image.variants.map((v) => `${v.url} ${v.width}w`).join(", ");
  const id = `mlx-review-${index}`;
  return (
    <article className={`mlx-rev ${long ? "mlx-rev--long" : ""} ${open ? "mlx-rev--open" : ""}`}>
      <div className="mlx-rev__img">
        <img src={review.image.variants[0].url} srcSet={srcSet} sizes="(min-width:1024px) 416px, 82vw" width={review.image.width} height={review.image.height} alt={review.image.alt} loading="lazy" decoding="async" />
      </div>
      <div className="mlx-rev__top">
        <h3 className="mlx-rev__name">{review.name}</h3>
        <div className="mlx-stars" role="img" aria-label={`${review.rating}/5`}>
          {Array.from({ length: review.rating }).map((_, i) => <Star key={i} className="h-5 w-5" fill="currentColor" strokeWidth={0} aria-hidden="true" />)}
        </div>
      </div>
      <p id={id} className="mlx-rev__txt">{review.text}</p>
      {long && (
        <button type="button" className="mlx-rev__tog" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>
          {open ? t("common.show_less", { lng: "en" }) : t("landing.source_ui.read_full_review", { lng: "en", defaultValue: "Read full review" })}
        </button>
      )}
    </article>
  );
}

export function MustangCard({ car, index, localized = false }: { car: CarCardData; index: number; localized?: boolean }) {
  const { t } = useTranslation();
  const { formatPreferred } = useCurrency();
  const { isFavourited, toggle, loading } = useFavourite(car.id, {
    make: car.make, model: car.model, year: car.year, slug: car.slug, city: car.city ?? null, state: car.state ?? null,
    country_code: car.countryCode ?? null,
    primary_image: car.primaryImageCdnMedium ?? car.primaryImageCdnFull ?? car.primaryImageUrl ?? null,
    price_driving: car.priceDriving ?? null, price_event: car.priceEvent ?? null, minimum_price: car.minimumPrice ?? null,
    chauffeur_min_price: car.chauffeurMinPrice ?? null, event_min_price: car.eventMinPrice ?? null,
    rental_driving_active: car.rentalDrivingActive, rental_chauffeured_active: car.rentalChauffeurActive,
    rental_event_active: car.rentalEventActive, local_currency_code: car.currencyCode ?? null,
  });
  const img = car.primaryImageCdnMedium ?? car.primaryImageCdnFull ?? car.primaryImageUrl;
  const measuredImage = car.images?.find(image => image.url === img);
  const { price, unit } = getDisplayPrice(car);
  const formatted = price != null ? formatPreferred(price, car.currencyCode || "USD") : null;
  const place = [car.city, car.state].filter(Boolean).join(", ");
  const name = `${car.year} ${car.make} ${car.model}`;
  const path = buildCarPath(car);
  const href = path ? `${import.meta.env.BASE_URL || "/"}${path.replace(/^\//, "")}` : undefined;
  const rating = car.reviewsRating != null && (car.reviewsCount ?? 0) > 0 ? car.reviewsRating : null;
  return (
    <div className="mlx-card" data-car-id={car.id} data-car-country={car.countryCode}>
      <div className="mlx-card__head">
        <a href={href} aria-disabled={!href || undefined} className="mlx-card__img" style={{ display: "block" }} aria-label={name}>
          {img && <img src={img} alt={name} width={measuredImage?.width ?? undefined} height={measuredImage?.height ?? undefined} loading={index < 3 ? "eager" : "lazy"} decoding="async" />}
        </a>
        <button type="button" className="mlx-fav" aria-pressed={isFavourited} disabled={loading} aria-label={localized ? t(isFavourited ? "common.remove_from_favorites" : "common.add_to_favorites") : isFavourited ? "Remove from favorites" : "Add to favorites"} onClick={() => void toggle()}>
          <Heart className="h-5 w-5" fill={isFavourited ? "currentColor" : "none"} aria-hidden="true" />
        </button>
      </div>
      <a href={href} aria-disabled={!href || undefined} style={{ color: "inherit", textDecoration: "none", display: "block" }}>
        <div className="mlx-card__name">{name}</div>
        <div className="mlx-card__row">
          <span>{place && <>{!localized && t("landing.source_ui.delivered_from", { lng: "en", defaultValue: "Delivered from " })}<b>{place}</b></>}</span>
          {formatted && <span className="mlx-price">{formatted}{unit === "day" && <small>{t("common.per_day", localized ? undefined : { lng: "en" })}</small>}{unit === "hour" && <small>{t("common.per_hour", localized ? undefined : { lng: "en" })}</small>}</span>}
        </div>
        {rating != null && (
          <div className="mlx-rating"><Star className="h-4 w-4" fill="currentColor" strokeWidth={0} aria-hidden="true" />{Number(rating).toFixed(1)}<span>({car.reviewsCount})</span></div>
        )}
      </a>
      {!href && <p role="status">{t("carCard.link_unavailable", { defaultValue: "Listing link temporarily unavailable" })}</p>}
    </div>
  );
}

export default function MustangLiveLanding({ searchState, cars, loading, unavailable, fetching, onRetry, dataAttrs, seoSupplement }: {
  searchState: SearchState;
  cars: CarCardData[];
  loading: boolean;
  unavailable: boolean;
  fetching: boolean;
  onRetry: () => void;
  dataAttrs?: Record<string, string | undefined>;
  seoSupplement: {
    faqs: { q: string; a: string }[];
    internalLinks: { href: string; label: string; section: string }[];
  };
}) {
  const { t } = useTranslation();
  const track = useRef<HTMLDivElement>(null);
  const hero = source.hero;

  const scrollReviews = (dir: 1 | -1) => {
    const el = track.current;
    const card = el?.querySelector("article");
    if (el && card) el.scrollBy({ left: dir * (card.getBoundingClientRect().width + 32), behavior: "smooth" });
  };

  return (
    <div className="mustang-source-landing" data-mustang-seo="restored" {...dataAttrs}>
      <section className="mlx-hero">
        <img src={hero.variants[2].url} srcSet={hero.variants.map((v) => `${v.url} ${v.width}w`).join(", ")} sizes="100vw" width={hero.width} height={hero.height} alt={hero.alt} fetchPriority="high" decoding="async" />
        <div className="mlx-center mlx-hero__in">
          <div className="mlx-hero__box">
            <h1>{source.heading}</h1>
            <p>{source.subtitle}</p>
            <MustangLocationSearch searchState={searchState} />
          </div>
        </div>
      </section>

      <section className="mlx-copy">
        <div className="mlx-copy__in" dangerouslySetInnerHTML={{ __html: source.introHtml }} />
      </section>

      <section className="mlx-cars">
        <div className="mlx-center">
          <h2 className="mlx-title">{t("landing.source_ui.mustang_inventory", { lng: "en", defaultValue: "Rent a Ford Mustang" })}</h2>
          {loading ? (
            <div className="mlx-grid" aria-busy="true">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="mlx-skel" />)}</div>
          ) : unavailable ? (
            <div className="mlx-note" role="alert">
              <p>{t("landing.source_ui.mustang_unavailable", { lng: "en", defaultValue: "We could not load today's Mustang listings." })}</p>
              <p style={{ marginTop: 16 }}><button type="button" className="mlx-btn" onClick={onRetry} disabled={fetching}>{t("common.try_again", { lng: "en" })}</button></p>
            </div>
          ) : cars.length === 0 ? (
            <div className="mlx-note">
              <p>{t("landing.source_ui.mustang_empty", { lng: "en", defaultValue: "No Mustangs are listed right now. Search for a car near you instead." })}</p>
            </div>
          ) : (
            <div className="mlx-grid">
              {cars.map((car, i) => <MustangCard key={car.id} car={car} index={i} />)}
            </div>
          )}
          <div className="mlx-more">
            <SearchStateLink state={searchState} className="mlx-btn" title={t("landing.source_ui.browse", { lng: "en", defaultValue: "Explore more cars" })}>{t("landing.source_ui.browse", { lng: "en", defaultValue: "Explore more cars" })}</SearchStateLink>
          </div>
        </div>
      </section>

      <section className="mlx-reviews" aria-labelledby="mlx-reviews-title">
        <div className="mlx-center">
          <div className="mlx-reviews__head">
            <h2 id="mlx-reviews-title">{t("nav.reviews", { lng: "en" })}</h2>
            <div className="mlx-arrows">
              <button type="button" aria-label={t("landing.source_ui.previous_reviews", { lng: "en", defaultValue: "Previous reviews" })} onClick={() => scrollReviews(-1)}>&lsaquo;</button>
              <button type="button" aria-label={t("landing.source_ui.next_reviews", { lng: "en", defaultValue: "Next reviews" })} onClick={() => scrollReviews(1)}>&rsaquo;</button>
            </div>
          </div>
          <div className="mlx-track" ref={track}>
            {source.reviews.map((r, i) => <ReviewCard key={r.name} review={r} index={i} />)}
          </div>
        </div>
      </section>

      <section className="mlx-host">
        <div className="mlx-copy__in">
          <div dangerouslySetInnerHTML={{ __html: source.hostHtml }} />
          <Link href="/list-a-car" className="mlx-btn mlx-btn--dark">{t("nav.list_a_car", { lng: "en" })}</Link>
        </div>
      </section>

      {(seoSupplement.faqs.length > 0 || seoSupplement.internalLinks.length > 0) && (
        <section className="mlx-seo" data-mustang-seo-supplement="true">
          {seoSupplement.faqs.length > 0 && (
            <div className="mlx-copy__in">
              <h2 className="mlx-title">{t("landing.faq_heading", { lng: "en" })}</h2>
              <div className="mlx-faqs">
                {seoSupplement.faqs.map((faq, index) => (
                  <details key={`${faq.q}-${index}`}>
                    <summary>{faq.q}</summary>
                    <p>{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          )}
          {seoSupplement.internalLinks.length > 0 && (
            <nav className="mlx-center" aria-label={t("landing.source_ui.related", { lng: "en", defaultValue: "Related rental pages" })}>
              <h2 className="mlx-title">{t("landing.explore_more", { lng: "en" })}</h2>
              {[...new Set(seoSupplement.internalLinks.map((link) => link.section))].map((section) => (
                <div className="mlx-seo-group" key={section}>
                  <h3>{section}</h3>
                  <ul className="mlx-seo-links">
                    {seoSupplement.internalLinks.filter((link) => link.section === section).map((link) => (
                      <li key={link.href}><Link href={link.href}>{link.label}<span aria-hidden="true"> &rarr;</span></Link></li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          )}
        </section>
      )}
    </div>
  );
}
